import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  DatePicker, 
  Select, 
  message, 
  Popconfirm,
  Typography,
  Tag,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CalendarOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { giftService, Event, CreateEventRequest, UpdateEventRequest } from '../../../services/giftService';
import dayjs from 'dayjs';
import styles from './EventsPageMobile.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EventsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [form] = Form.useForm();

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await giftService.getEvents();
      setEvents(response.data);
    } catch (error) {
      message.error('Không thể tải danh sách sự kiện');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleCreate = () => {
    setEditingEvent(null);
    form.resetFields();
    form.setFieldsValue({
      event_type: 'other'
    });
    setModalVisible(true);
  };

  const handleEdit = (record: Event) => {
    setEditingEvent(record);
    form.setFieldsValue({
      name: record.name,
      event_date: dayjs(record.event_date),
      event_type: record.event_type,
      description: record.description
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await giftService.deleteEvent(id);
      message.success('Xóa sự kiện thành công');
      fetchEvents();
      setDetailModalVisible(false);
    } catch (error) {
      message.error('Không thể xóa sự kiện');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        event_date: values.event_date.format('YYYY-MM-DD')
      };

      if (editingEvent) {
        await giftService.updateEvent(editingEvent.id, data);
        message.success('Cập nhật sự kiện thành công');
      } else {
        await giftService.createEvent(data);
        message.success('Tạo sự kiện thành công');
      }

      setModalVisible(false);
      fetchEvents();
    } catch (error) {
      message.error('Có lỗi xảy ra');
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      wedding: 'Đám cưới',
      housewarming: 'Tân gia',
      birthday: 'Sinh nhật',
      graduation: 'Tốt nghiệp',
      other: 'Khác'
    };
    return labels[eventType] || eventType;
  };

  const getEventTypeColor = (eventType: string) => {
    const colors: Record<string, string> = {
      wedding: '#ff6b8a',
      housewarming: '#52c41a',
      birthday: '#1890ff',
      graduation: '#722ed1',
      other: '#faad14'
    };
    return colors[eventType] || '#faad14';
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setDetailModalVisible(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mobileShell}>
        {/* Event List */}
        <div className={styles.eventList}>
          <div className={styles.listHeader}>
            <div className={styles.headerLeft}>
              <h3>Sự kiện ({events.length})</h3>
            </div>
            <div className={styles.headerRight}>
              <div className={styles.addButton} onClick={handleCreate}>
                <PlusOutlined />
              </div>
            </div>
          </div>

          <div className={styles.eventsList}>
            {events.length > 0 ? (
              events.map((event) => (
                <div
                  key={event.id}
                  className={styles.eventCard}
                  onClick={() => handleEventClick(event)}
                >
                  <div className={styles.iconContainer} style={{ backgroundColor: getEventTypeColor(event.event_type) }}>
                    <CalendarOutlined />
                  </div>
                  <div className={styles.eventDetails}>
                    <div className={styles.eventTitle}>
                      {event.name}
                    </div>
                    <div className={styles.eventInfo}>
                      <Text type="secondary">
                        {dayjs(event.event_date).format('DD/MM/YYYY')}
                      </Text>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📅</div>
                <div className={styles.emptyText}>Chưa có sự kiện nào</div>
                <div className={styles.emptySubtext}>Tạo sự kiện đầu tiên để bắt đầu</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        title={editingEvent ? 'Chỉnh sửa sự kiện' : 'Tạo sự kiện mới'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={320}
        centered
        className={styles.modal}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            event_type: 'other'
          }}
        >
          <Form.Item
            name="name"
            label="Tên sự kiện"
            rules={[{ required: true, message: 'Vui lòng nhập tên sự kiện' }]}
          >
            <Input placeholder="Ví dụ: Lễ cưới Minh & Anh" />
          </Form.Item>

          <Form.Item
            name="event_date"
            label="Ngày diễn ra"
            rules={[{ required: true, message: 'Vui lòng chọn ngày diễn ra' }]}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Chọn ngày"
            />
          </Form.Item>

          <Form.Item
            name="event_type"
            label="Loại sự kiện"
            rules={[{ required: true, message: 'Vui lòng chọn loại sự kiện' }]}
          >
            <Select placeholder="Chọn loại sự kiện">
              <Option value="wedding">Đám cưới</Option>
              <Option value="housewarming">Tân gia</Option>
              <Option value="birthday">Sinh nhật</Option>
              <Option value="graduation">Tốt nghiệp</Option>
              <Option value="other">Khác</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
          >
            <TextArea 
              rows={3} 
              placeholder="Mô tả chi tiết về sự kiện (tùy chọn)"
            />
          </Form.Item>

                     <Form.Item>
             <Space style={{ width: '100%' }}>
               <Button 
                 type="primary" 
                 htmlType="submit" 
                 style={{ flex: 1, backgroundColor: '#ff89a1', borderColor: '#ff89a1' }}
               >
                 {editingEvent ? 'Cập nhật' : 'Tạo sự kiện'}
               </Button>
               <Button 
                 onClick={() => setModalVisible(false)}
                 style={{ flex: 1 }}
               >
                 Hủy
               </Button>
             </Space>
           </Form.Item>
        </Form>
      </Modal>

             {/* Detail Modal */}
       {selectedEvent && detailModalVisible && (
         <div className={styles.detailModal} onClick={() => setDetailModalVisible(false)}>
          <div className={styles.detailContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.detailHeader}>
              <div className={styles.detailIcon} style={{ backgroundColor: getEventTypeColor(selectedEvent.event_type) }}>
                <CalendarOutlined />
              </div>
              <div className={styles.detailInfo}>
                <h3>{selectedEvent.name}</h3>
                <div className={styles.detailTags}>
                  <Tag color={getEventTypeColor(selectedEvent.event_type)} className={styles.eventTypeTag}>
                    {getEventTypeLabel(selectedEvent.event_type)}
                  </Tag>
                  <Tag color={selectedEvent.is_active ? 'green' : 'red'} className={styles.statusTag}>
                    {selectedEvent.is_active ? 'Hoạt động' : 'Không hoạt động'}
                  </Tag>
                </div>
              </div>
                             <button
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   console.log('Close button clicked');
                   setDetailModalVisible(false);
                 }}
                 className={styles.closeButton}
                 type="button"
               >
                 <CloseOutlined />
               </button>
            </div>

                         <div className={styles.detailBody}>
               <div className={styles.detailItem}>
                 <Text strong>Ngày diễn ra:</Text>
                 <Text>{dayjs(selectedEvent.event_date).format('DD/MM/YYYY')}</Text>
               </div>
               {selectedEvent.description && (
                 <div className={styles.detailItem}>
                   <Text strong>Mô tả:</Text>
                   <Text>{selectedEvent.description}</Text>
                 </div>
                               )}
              </div>

                         <div className={styles.detailActions}>
               <Button
                 type="primary"
                 icon={<EditOutlined />}
                 onClick={() => {
                   setDetailModalVisible(false);
                   handleEdit(selectedEvent);
                 }}
                 className={styles.detailActionButton}
                 style={{ backgroundColor: '#ff89a1', borderColor: '#ff89a1' }}
               >
                 Chỉnh sửa
               </Button>
              <Button
                type="default"
                onClick={() => navigate(`/gifts?event_id=${selectedEvent.id}`)}
                className={styles.detailActionButton}
              >
                Xem tiền mừng
              </Button>
              <Popconfirm
                title="Bạn có chắc muốn xóa sự kiện này?"
                onConfirm={() => handleDelete(selectedEvent.id)}
                okText="Có"
                cancelText="Không"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  className={styles.detailActionButton}
                >
                  Xóa
                </Button>
              </Popconfirm>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPageMobile;
